import isURL from 'validator/lib/isURL';
import { trim } from 'lodash';

import {
    removeHtmlTags,
    checkStringContainsNumbers,
    validateZipCode,
} from '../../../helper';
import { SECTION_ORDER, getRequiredFields, FIELD_LABELS, TRAVEL_NEEDS_INFO } from './fields';

/**
 * Validation for the program entry form, in two layers.
 *
 *  - `validateProgram` is blocking. It is `AddProgramModal.js:684-937` ported field for
 *    field, with the three agreed tightenings noted inline.
 *  - `getValidationIssues` is advisory. It comes from
 *    `Program Entry Updates/src/utils/validation.ts` and produces inline notes that never
 *    stop a save — price ordering, overlapping windows, and so on.
 *
 * The app stores dates as epoch milliseconds, so ordering comparisons are numeric here
 * where the prototype compared ISO strings lexically.
 */

const num = value => {
    if (value === undefined || value === null || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const both = (a, b) => Boolean(num(a) !== null) && Boolean(num(b) !== null);

/**
 * Strip HTML and trim, safely.
 *
 * `removeHtmlTags` (`helper.js:191`) has no else branch, so it returns `undefined` for
 * any falsy input — including the empty string every field starts as. Calling `.trim()`
 * on that result directly throws, so every caller here goes through this instead.
 */
const plainText = value => (removeHtmlTags(String(value ?? '')) || '').trim();

/**
 * True when a value counts as filled for the progress meter.
 * Rich-text fields arrive as HTML, so tags are stripped before the emptiness test.
 */
export const isFilled = value => {
    if (typeof value === 'boolean') return true;
    if (typeof value === 'number') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined || value === '') return false;
    return plainText(value).length > 0;
};

/**
 * Advisory issues — shown inline and counted in each section header, never blocking.
 *
 * @param values   userInput
 * @param rows     additionalDates
 * @param byDates  1 = program dates, 2 = registration windows
 */
export const getValidationIssues = (values, rows, byDates) => {
    const issues = [];
    const usesProgramDates = byDates === 1;

    // ---- Applicable groups -------------------------------------------------
    const minAge = num(values.minAge);
    const maxAge = num(values.maxAge);
    if (minAge !== null && maxAge !== null && maxAge < minAge) {
        issues.push({
            key: 'age-range',
            section: 'groups',
            field: 'maxAge',
            severity: 'error',
            message: `Max age must be ${values.minAge} or higher.`,
        });
    }

    // ---- Registration windows ----------------------------------------------
    if (!usesProgramDates) {
        const windows = [
            {
                name: 'Early Bird',
                start: values.dateEarlyRegisterOpen,
                end: values.dateEarlyRegisterClosed,
                endKey: 'dateEarlyRegisterClosed',
            },
            {
                name: 'Standard',
                start: values.registerOpenDate,
                end: values.registerCloseDate,
                endKey: 'registerCloseDate',
            },
            {
                name: 'Late',
                start: values.dateLateRegisterOpen,
                end: values.dateLateRegisterClosed,
                endKey: 'dateLateRegisterClosed',
            },
        ];

        windows.forEach(window => {
            if (both(window.start, window.end) && num(window.end) < num(window.start)) {
                issues.push({
                    key: `${window.endKey}-order`,
                    section: 'price',
                    field: window.endKey,
                    severity: 'error',
                    message: `${window.name} registration must end on or after it starts.`,
                });
            }
        });

        if (
            both(values.dateEarlyRegisterClosed, values.registerOpenDate) &&
            num(values.dateEarlyRegisterClosed) >= num(values.registerOpenDate)
        ) {
            issues.push({
                key: 'early-standard-overlap',
                section: 'price',
                field: 'registerOpenDate',
                severity: 'error',
                message: 'Standard registration must start after Early Bird ends.',
            });
        }

        if (
            both(values.registerCloseDate, values.dateLateRegisterOpen) &&
            num(values.registerCloseDate) >= num(values.dateLateRegisterOpen)
        ) {
            issues.push({
                key: 'standard-late-overlap',
                section: 'price',
                field: 'dateLateRegisterOpen',
                severity: 'error',
                message: 'Late registration must start after Standard ends.',
            });
        }

        // ---- Price ordering (advisory) --------------------------------------
        const early = num(values.earlyBirdPrice);
        const standard = num(values.registrationFee);
        const late = num(values.lateRegistrationFee);

        if (early !== null && standard !== null && early > standard) {
            issues.push({
                key: 'early-price',
                section: 'price',
                field: 'earlyBirdPrice',
                severity: 'warning',
                message: 'Early Bird price should not exceed the Standard price.',
            });
        }

        if (late !== null && standard !== null && late < standard) {
            issues.push({
                key: 'late-price',
                section: 'price',
                field: 'lateRegistrationFee',
                severity: 'warning',
                message: 'Late price should not be lower than the Standard price.',
            });
        }

        // The prototype carried a commuter price per tier. The API has one for the whole
        // program, so the form offers it on the Standard row and compares it against the
        // standard price only.
        const commuter = num(values.commuterPrice);
        if (commuter !== null && standard !== null && standard > 0 && commuter > standard) {
            issues.push({
                key: 'commuter-above-overnight',
                section: 'price',
                field: 'commuterPrice',
                severity: 'warning',
                message: 'Commuter price is higher than the overnight price.',
            });
        }

        // Registration closing after the program has already finished.
        if (
            both(values.registerCloseDate, values.endDate) &&
            num(values.registerCloseDate) > num(values.endDate)
        ) {
            issues.push({
                key: 'registration-after-program',
                section: 'price',
                field: 'registerCloseDate',
                severity: 'warning',
                message: 'Standard registration closes after the program ends.',
            });
        }
    }

    // ---- Program date blocks ------------------------------------------------
    if (usesProgramDates) {
        (rows || []).forEach((row, index) => {
            if (both(row.startDate, row.endDate) && num(row.endDate) < num(row.startDate)) {
                issues.push({
                    key: `row-${index}-order`,
                    section: 'price',
                    rowIndex: index,
                    rowField: 'endDate',
                    severity: 'error',
                    message: `Date block ${index + 1} ends before it starts.`,
                });
            }
        });

        const filled = (rows || [])
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => row.startDate && row.endDate);

        filled.forEach(({ row, index }, i) => {
            filled.slice(i + 1).forEach(other => {
                if (
                    num(row.startDate) <= num(other.row.endDate) &&
                    num(other.row.startDate) <= num(row.endDate)
                ) {
                    issues.push({
                        key: `row-overlap-${index}-${other.index}`,
                        section: 'price',
                        rowIndex: other.index,
                        rowField: 'startDate',
                        severity: 'warning',
                        message: 'This date block overlaps another block.',
                    });
                }
            });
        });
    }

    // ---- Program dates & time ------------------------------------------------
    if (
        !usesProgramDates &&
        both(values.startDate, values.endDate) &&
        num(values.endDate) < num(values.startDate)
    ) {
        issues.push({
            key: 'program-date-order',
            section: 'schedule',
            field: 'endDate',
            severity: 'error',
            message: 'End date must be on or after the start date.',
        });
    }

    return issues;
};

/**
 * Blocking validation — the port of `handleValidate`.
 *
 * Returns the error object rather than calling a setter, so the caller decides what to do
 * with it. `rows` comes back too because the original writes per-row errors into
 * `additionalDates` itself (`AddProgramModal.js:790-798`).
 *
 * Three deliberate tightenings over the original, all agreed in the decisions doc:
 *   1. `programType` is required (its check is commented out at `:746-749`)
 *   2. `registrationFee` and `commuterPrice` are required — both carry a `*` but neither
 *      was ever checked
 *   3. `registerOpenDate` is checked against itself; the original tested
 *      `registerCloseDate` twice and reported the first failure against `registerOpenDate`
 */
export const validateProgram = ({ userInput, byDates, additionalDates, cmQuestions }) => {
    const errors = {};
    let valid = true;
    let rows = additionalDates;
    let questionsError = '';

    const fail = (field, message) => {
        errors[field] = message;
        valid = false;
    };

    const {
        programName,
        noOfSlots,
        registerOpenDate,
        registerCloseDate,
        registrationFee,
        category,
        subCategory,
        addressLine1,
        city,
        state,
        gender,
        programType,
        minAge,
        maxAge,
        grade,
        expLevel,
        endDate,
        checkInTime,
        checkOutTime,
        tshirt,
        equipment,
        facility,
        travel,
        travelInfo,
        scholarships,
        scholarshipsUrl,
        aboutProgram,
        disclaimer,
        isOnline,
        location,
        zipCode,
        programTimezone,
    } = userInput;

    // ---- Description ---------------------------------------------------------
    // The `String(x ?? '')` wrappers throughout guard against nulls arriving from
    // `dProgramDetail` on an edit; the old form calls `.trim()` on these directly.
    if (String(programName ?? '').trim() === '') {
        fail('programName', 'Program name is required');
    } else if (programName.length > 100) {
        fail('programName', 'Program name is too large max limit is 100 characters');
    }

    if (category === '') fail('category', 'Category is required');
    if (subCategory === '' || (Array.isArray(subCategory) && !subCategory.length)) {
        fail('subCategory', 'Sub category is required');
    }
    if (programType === '') fail('programType', 'Program type is required');

    if (plainText(aboutProgram) === '') fail('aboutProgram', 'About program is required');
    if (plainText(disclaimer) === '') fail('disclaimer', 'Disclaimer is required');

    // ---- Price / dates -------------------------------------------------------
    if (byDates !== 1) {
        if (trim(String(registerOpenDate)) === '') {
            fail('registerOpenDate', 'Date standard registration start is required');
        }
        if (registerCloseDate === '') {
            fail('registerCloseDate', 'Date standard registration end is required');
        }
        if (endDate === '') fail('endDate', 'Start end date is required');
        if (noOfSlots === '') fail('noOfSlots', 'Number of slots is required');
        if (registrationFee === '' || registrationFee === null) {
            fail('registrationFee', 'Standard price is required');
        }
        // Commuter price is intentionally not checked — not every camp offers a commuter
        // option, so an empty value is valid. `index.js` sends 0 in that case.
    } else {
        rows = additionalDates.map(item => ({
            ...item,
            endDateErr: !item.startDate || !item.endDate ? 'Date is required' : '',
        }));
        if (rows.filter(item => item.endDateErr).length) valid = false;
    }

    // ---- Applicable groups ---------------------------------------------------
    if (gender === '') fail('gender', 'Gender is required');
    if (expLevel === '') fail('expLevel', 'Experience level is required');
    if (minAge === '') fail('minAge', 'Min age is required');
    if (maxAge === '') fail('maxAge', 'Max age is required');
    if (grade === '' || (Array.isArray(grade) && !grade.length)) fail('grade', 'Grade is required');

    // ---- Location ------------------------------------------------------------
    if (!isOnline) {
        if (location === '') fail('location', 'Location is required');
        if (programTimezone === '') fail('programTimezone', 'Program timezone is required');
        if (addressLine1 === '') fail('addressLine1', 'Street address is required');
        if (city === '') fail('city', 'City is required');
        if (state === '') fail('state', 'State is required');

        if (zipCode) {
            if (!checkStringContainsNumbers(zipCode)) {
                fail('zipCode', 'Zipcode is invalid');
            } else if (validateZipCode(zipCode)) {
                fail('zipCode', 'Zipcode is invalid');
            }
        }
    }

    // ---- Amenities -----------------------------------------------------------
    if (tshirt === '' || tshirt === null || tshirt === 'undefined') {
        fail('tshirt', 'T-shirt is required');
    }
    if (String(equipment ?? '').trim() === '') fail('equipment', 'Equipment is required');
    if (facility === '') fail('facility', 'Facility is required');

    if (scholarships === '' || scholarships === null || scholarships === 'undefined') {
        fail('scholarships', 'Scholarships is required');
    }
    if (scholarshipsUrl) {
        if (!isURL(scholarshipsUrl)) {
            fail('scholarshipsUrl', 'Scholarships url is not valid');
        } else if (!scholarshipsUrl.startsWith('http')) {
            fail(
                'scholarshipsUrl',
                'Scholarships url is not valid please add http:// or https:// Before your URL'
            );
        }
    }

    if (travel === '' || travel === null || travel === 'undefined') {
        fail('travel', 'Travel Info is required');
    }
    if (TRAVEL_NEEDS_INFO.includes(travel) && String(travelInfo ?? '').trim() === '') {
        fail('travelInfo', 'Travel Info is required');
    }

    // ---- Timing --------------------------------------------------------------
    if (checkInTime === '') fail('checkInTime', 'Check in time is required');
    if (checkOutTime === '') fail('checkOutTime', 'Check out time is required');

    // ---- Custom questions ----------------------------------------------------
    if (cmQuestions.some(ques => !ques.title || ques.title.trim() === '')) {
        questionsError = 'Question title is required';
        valid = false;
    }

    return { valid, errors, rows, questionsError };
};

/**
 * The required fields still empty, in section order — what the "fix these first" banner
 * lists and what the meter counts against.
 */
export const getMissingFields = (values, byDates) => {
    const required = getRequiredFields(values, byDates);
    const missing = [];

    SECTION_ORDER.forEach(({ id }) => {
        required[id].forEach(field => {
            if (!isFilled(values[field])) {
                missing.push({ field, section: id, label: FIELD_LABELS[field] || field });
            }
        });
    });

    return missing;
};
